import React, { useState } from 'react';
import { StarIcon, SearchIcon, ChevronDownIcon, EyeIcon, MessageSquareIcon, FlagIcon, BookOpenIcon, TrendingUpIcon, ClockIcon, MoreVerticalIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import BackButton from '../../components/UI/BackButton';
import { DisclosureBadgeGroup } from '../../components/Disclosure/DisclosureBadgeGroup';
import type { DisclosureId } from '../../data/disclosures';
// --- Types ---
interface ProductReview {
  id: string;
  reviewerName: string;
  reviewerInitials: string;
  reviewerColor: string;
  productTitle: string;
  productCover: string;
  rating: number;
  date: string;
  excerpt: string;
  fullReview: string;
  /** Reviewer-declared commercial relationship, shown as vendors see it. */
  disclosures?: DisclosureId[];
}
interface Product {
  id: string;
  title: string;
  cover: string;
  avgRating: number;
  totalReviews: number;
  ratingDistribution: number[]; // [5star, 4star, 3star, 2star, 1star] counts
}
// --- Mock Data ---
const products: Product[] = [{
  id: 'p1',
  title: 'The Silent Echo',
  cover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  avgRating: 4.2,
  totalReviews: 125,
  ratingDistribution: [60, 40, 15, 5, 5]
}, {
  id: 'p2',
  title: 'Beyond the Veil',
  cover: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  avgRating: 4.8,
  totalReviews: 89,
  ratingDistribution: [70, 15, 4, 0, 0]
}, {
  id: 'p3',
  title: 'Midnight Gardens',
  cover: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  avgRating: 3.9,
  totalReviews: 45,
  ratingDistribution: [15, 15, 10, 3, 2]
}, {
  id: 'p4',
  title: 'Echo Series Box Set',
  cover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  avgRating: 4.9,
  totalReviews: 12,
  ratingDistribution: [10, 2, 0, 0, 0]
}, {
  id: 'p5',
  title: 'Author Merch Bundle',
  cover: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  avgRating: 4.5,
  totalReviews: 8,
  ratingDistribution: [5, 2, 1, 0, 0]
}];
const reviews: ProductReview[] = [{
  id: 'r1',
  reviewerName: 'Sarah M.',
  reviewerInitials: 'SM',
  reviewerColor: 'bg-purple-500',
  productTitle: 'The Silent Echo',
  productCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 5,
  date: 'Oct 12, 2023',
  disclosures: ['verified-purchase'],
  excerpt: 'Absolutely incredible! The character development was masterful...',
  fullReview: 'Absolutely incredible! The character development was masterful and the plot twists kept me guessing until the very end.'
}, {
  id: 'r2',
  reviewerName: 'James K.',
  reviewerInitials: 'JK',
  reviewerColor: 'bg-blue-500',
  productTitle: 'The Silent Echo',
  productCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4,
  date: 'Oct 10, 2023',
  excerpt: 'Great thriller with solid pacing. The middle section dragged slightly...',
  fullReview: 'Great thriller with solid pacing. The middle section dragged slightly but the ending more than made up for it.'
}, {
  id: 'r3',
  reviewerName: 'Emily R.',
  reviewerInitials: 'ER',
  reviewerColor: 'bg-teal-500',
  productTitle: 'Beyond the Veil',
  productCover: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 5,
  date: 'Oct 8, 2023',
  excerpt: 'This is hands down the best fantasy book I have read this year...',
  fullReview: 'This is hands down the best fantasy book I have read this year. The world-building is phenomenal.'
}, {
  id: 'r4',
  reviewerName: 'David L.',
  reviewerInitials: 'DL',
  reviewerColor: 'bg-amber-500',
  productTitle: 'Beyond the Veil',
  productCover: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4,
  date: 'Oct 5, 2023',
  excerpt: 'Engaging story with beautiful prose. Some plot threads felt unresolved...',
  fullReview: 'Engaging story with beautiful prose. Some plot threads felt unresolved but I am eager for the sequel.'
}, {
  id: 'r5',
  reviewerName: 'Lisa P.',
  reviewerInitials: 'LP',
  reviewerColor: 'bg-pink-500',
  productTitle: 'Midnight Gardens',
  productCover: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 3,
  date: 'Oct 3, 2023',
  excerpt: 'Beautiful writing style but the pacing was too slow for my taste...',
  fullReview: 'Beautiful writing style but the pacing was too slow for my taste. The atmosphere was hauntingly good though.'
}, {
  id: 'r6',
  reviewerName: 'Tom W.',
  reviewerInitials: 'TW',
  reviewerColor: 'bg-green-500',
  productTitle: 'Echo Series Box Set',
  productCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 5,
  date: 'Sep 28, 2023',
  excerpt: 'Perfect collection! Having all three books together made for an amazing...',
  fullReview: 'Perfect collection! Having all three books together made for an amazing binge read experience.'
}, {
  id: 'r7',
  reviewerName: 'Anna C.',
  reviewerInitials: 'AC',
  reviewerColor: 'bg-indigo-500',
  productTitle: 'Author Merch Bundle',
  productCover: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4,
  date: 'Sep 25, 2023',
  excerpt: 'Love the quality of the merchandise. The bookmark set is gorgeous...',
  fullReview: 'Love the quality of the merchandise. The bookmark set is gorgeous and the tote bag is sturdy.'
}, {
  id: 'r8',
  reviewerName: 'Chris B.',
  reviewerInitials: 'CB',
  reviewerColor: 'bg-red-400',
  productTitle: 'The Silent Echo',
  productCover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 2,
  date: 'Sep 20, 2023',
  excerpt: 'Not my cup of tea. Found the protagonist unlikeable and the pacing...',
  fullReview: 'Not my cup of tea. Found the protagonist unlikeable and the pacing inconsistent throughout.'
}];
const ProductReviews = () => {
  const dk = useDarkClasses();
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter Logic
  const filteredReviews = reviews.filter(review => {
    const matchesProduct = selectedProduct === 'all' || review.productTitle === selectedProduct;
    const matchesRating = ratingFilter === 'all' || review.rating.toString() === ratingFilter;
    const matchesSearch = review.reviewerName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesProduct && matchesRating && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'highest':
        return b.rating - a.rating;
      case 'lowest':
        return a.rating - b.rating;
      default:
        // newest
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });
  // Stats
  const stats = {
    total: reviews.length,
    avgRating: (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1),
    fiveStar: reviews.filter(r => r.rating === 5).length,
    products: new Set(reviews.map(r => r.productTitle)).size
  };
  // Selected Product Details
  const activeProductDetails = products.find(p => p.title === selectedProduct);
  return <div className={`min-h-screen pb-12 ${dk.page}`} data-id="element-21375" data-mp="8mljn0du3025">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-21376" data-mp="8nd69lpgdnga">
        <div className="mb-6" data-id="element-21377" data-mp="3t8y4z4gb9dm">
          <BackButton data-id="element-21378" data-mp="3sz6z87o6w6c" />
        </div>

        {/* Header */}
        <div className="mb-8" data-id="element-21379" data-mp="3v1mpzhifu7aq">
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-21380" data-mp="3uys4dmnougfq">
            Product Reviews
          </h1>
          <p className={`mt-1 text-lg ${dk.textSecondary}`} data-id="element-21381" data-mp="3l2xhgfcflz75">
            See what readers are saying about your products
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-21382" data-mp="3h3gcdn0x342i">
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-21383" data-mp="1wijacyn2zc3a">
            <div className="flex items-center justify-between" data-id="element-21384" data-mp="20kd5kreibnw2">
              <div data-id="element-21385" data-mp="20mr45a8ebi8d">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21386" data-mp="25k93c8wm2j36">
                  Total Reviews
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-21387" data-mp="1cvbvkzq104oo">
                  {stats.total}
                </p>
              </div>
              <div className={`p-2 rounded-full ${dk.bgMuted}`} data-id="element-21388" data-mp="1hvgawmf76tdb">
                <StarIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-21389" data-mp="1mtrlx0h8b9x4" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-21390" data-mp="1rkhs2fxhbgsv">
            <div className="flex items-center justify-between" data-id="element-21391" data-mp="u53wqttd00ea">
              <div data-id="element-21392" data-mp="u4kfzgfg3i1u">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21393" data-mp="u2zs29nmyxcy">
                  Avg Rating
                </p>
                <p className="text-2xl font-bold mt-1 text-amber-600" data-id="element-21394" data-mp="1uxkavfuddsv4">
                  {stats.avgRating}★
                </p>
              </div>
              <div className="p-2 rounded-full bg-amber-100" data-id="element-21395" data-mp="24vgqajucb9z1">
                <TrendingUpIcon className="h-5 w-5 text-amber-600" data-id="element-21396" data-mp="24u5jsc9ifo48" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-21397" data-mp="1c7kstcwy5e17">
            <div className="flex items-center justify-between" data-id="element-21398" data-mp="1c5htg29wm8jt">
              <div data-id="element-21399" data-mp="1h777lqsfb2c4">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21400" data-mp="1h6p4qbb99pv8">
                  5-Star Reviews
                </p>
                <p className="text-2xl font-bold mt-1 text-green-600" data-id="element-21401" data-mp="1qxs335csvqqt">
                  {stats.fiveStar}
                </p>
              </div>
              <div className="p-2 rounded-full bg-green-100" data-id="element-21402" data-mp="yfxtf47pjpe7">
                <StarIcon className="h-5 w-5 text-green-600" data-id="element-21403" data-mp="ye3atwjws9da" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-21404" data-mp="3bo3as04qrjh4">
            <div className="flex items-center justify-between" data-id="element-21405" data-mp="37p3yfugokkfn">
              <div data-id="element-21406" data-mp="32oq2hasct0xz">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21407" data-mp="32o7whmfw38cv">
                  Products Reviewed
                </p>
                <p className="text-2xl font-bold mt-1 text-blue-600" data-id="element-21408" data-mp="2ssd9yg8jot4t">
                  {stats.products}
                </p>
              </div>
              <div className="p-2 rounded-full bg-blue-100" data-id="element-21409" data-mp="2nyiiurqm1bvp">
                <BookOpenIcon className="h-5 w-5 text-blue-600" data-id="element-21410" data-mp="2k02vgmqma1hr" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className={`rounded-lg shadow-sm border p-4 mb-6 ${dk.card} ${dk.border}`} data-id="element-21411" data-mp="2a0u503uyn505">
          <div className="flex flex-col gap-4" data-id="element-21412" data-mp="2520py700z5xv">
            <div className="flex flex-col lg:flex-row gap-4 justify-between" data-id="element-21413" data-mp="252ahhz0pxl5o">
              {/* Product Selector */}
              <div className="relative w-full lg:w-1/3" data-id="element-21414" data-mp="1vg7zex4zf53r">
                <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)} className={`w-full pl-3 pr-10 py-2 text-sm border rounded-md appearance-none focus:ring-1 focus:ring-purple-500 ${dk.input}`} data-id="element-21415" data-mp="20e9e75vdki9f">
                  <option value="all" data-id="element-21416" data-mp="2e5o13nsel3lr">All Products</option>
                  {products.map(p => <option key={p.id} value={p.title} data-id="element-21417" data-mp="2j2eeu4gqjn40">
                      {p.title}
                    </option>)}
                </select>
                <ChevronDownIcon className={`absolute right-3 top-2.5 h-4 w-4 pointer-events-none ${dk.textMuted}`} data-id="element-21418" data-mp="29do526fgeuug" />
              </div>

              {/* Search & Sort */}
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" data-id="element-21419" data-mp="31uqqxix6y2rc">
                <div className="relative flex-grow" data-id="element-21420" data-mp="qf8cd3iordmj">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-21421" data-mp="qdx7t1hf21ud" />
                  <input type="text" placeholder="Search reviewer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ${dk.input}`} data-id="element-21422" data-mp="1051llxvcvgnm" />
                </div>
                <div className="relative min-w-[140px]" data-id="element-21423" data-mp="hea4ub91gnz7">
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} className={`w-full pl-3 pr-8 py-2 text-sm border rounded-md appearance-none focus:ring-1 focus:ring-purple-500 ${dk.input}`} data-id="element-21424" data-mp="hfuze0vcs6x2">
                    <option value="newest" data-id="element-21425" data-mp="1nuchp21rgmvf">Newest</option>
                    <option value="oldest" data-id="element-21426" data-mp="3tjfzgtrl7mj1">Oldest</option>
                    <option value="highest" data-id="element-21427" data-mp="3tkz9w3qkb3cw">Highest Rating</option>
                    <option value="lowest" data-id="element-21428" data-mp="2dehe197fsnw">Lowest Rating</option>
                  </select>
                  <ChevronDownIcon className={`absolute right-3 top-2.5 h-4 w-4 pointer-events-none ${dk.textMuted}`} data-id="element-21429" data-mp="6d5hwwoj9n5m" />
                </div>
              </div>
            </div>

            {/* Rating Tabs */}
            <div className="overflow-x-auto pb-2 lg:pb-0 scrollbar-hide border-t pt-4 border-gray-100 dark:border-gray-700" data-id="element-21430" data-mp="gajsifnrzhq0">
              <div className="flex space-x-2" data-id="element-21431" data-mp="q1nywqzdxu98">
                {['all', '5', '4', '3', '2', '1'].map(rating => <button key={rating} onClick={() => setRatingFilter(rating)} className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${ratingFilter === rating ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' : `${dk.textMuted} hover:${dk.bgMuted}`}`} data-id="element-21432" data-mp="u2pxr6pe44ao">
                    {rating === 'all' ? 'All Ratings' : `${rating}★`}
                  </button>)}
              </div>
            </div>
          </div>
        </div>

        {/* Product Summary Card (if specific product selected) */}
        {activeProductDetails && <div className={`mb-6 p-4 rounded-lg border flex flex-col sm:flex-row gap-6 items-center ${dk.card} ${dk.border}`} data-id="element-21433" data-mp="1wwws4a8eypb4">
            <img src={activeProductDetails.cover} alt={activeProductDetails.title} className="h-24 w-16 object-cover rounded shadow-sm" data-id="element-21434" data-mp="1ry2b9du0batn" />
            <div className="flex-1 w-full" data-id="element-21435" data-mp="1j4gw3tdxjrjp">
              <h3 className={`font-bold text-lg ${dk.textHeading}`} data-id="element-21436" data-mp="2fmmp93znfn67">
                {activeProductDetails.title}
              </h3>
              <div className="flex items-center gap-2 mt-1 mb-3" data-id="element-21437" data-mp="2av3ht337lizp">
                <div className="flex text-amber-400" data-id="element-21438" data-mp="8yw4r93idlwa">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < Math.round(activeProductDetails.avgRating) ? 'fill-current' : 'text-gray-300'}`} data-id="element-21439" data-mp="dy8mxgjfq4sj" data-mpi={i} />)}
                </div>
                <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-21440" data-mp="4630snfv6hl9">
                  {activeProductDetails.avgRating}
                </span>
                <span className={`text-sm ${dk.textMuted}`} data-id="element-21441" data-mp="3rd51gvvbwm82">
                  ({activeProductDetails.totalReviews} reviews)
                </span>
              </div>
            </div>
            {/* Mini Rating Distribution */}
            <div className="w-full sm:w-48 flex flex-col gap-1" data-id="element-21442" data-mp="3hf7dm9hdsrfz">
              {[5, 4, 3, 2, 1].map((star, idx) => {
            const count = activeProductDetails.ratingDistribution[idx];
            const max = Math.max(...activeProductDetails.ratingDistribution);
            return <div key={star} className="flex items-center text-xs gap-2" data-id="element-21443" data-mp="305myco5ms9he" data-mpi={idx}>
                    <span className={`w-3 ${dk.textMuted}`} data-id="element-21444" data-mp="2v71y4oqdppsy">{star}</span>
                    <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-21445" data-mp="39un1mjr61r1e">
                      <div className="h-full bg-amber-400 rounded-full" style={{
                  width: `${count / max * 100}%`
                }} data-id="element-21446" data-mp="353ogn6v8g4rt" />
                    </div>
                  </div>;
          })}
            </div>
          </div>}

        {/* Reviews List */}
        {filteredReviews.length === 0 ? <div className={`text-center py-12 rounded-lg border border-dashed ${dk.border} ${dk.bgMuted}/30`} data-id="element-21447" data-mp="3nv7k1et65kud">
            <SearchIcon className={`mx-auto h-12 w-12 ${dk.textMuted}`} data-id="element-21448" data-mp="1yhks28nhi1qq" />
            <h3 className={`mt-2 text-sm font-medium ${dk.textHeading}`} data-id="element-21449" data-mp="1tihoytr2e5le">
              No reviews found
            </h3>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-21450" data-mp="17ln280l6lbij">
              Try adjusting your search or filters.
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setRatingFilter('all');
          setSelectedProduct('all');
          setSortBy('newest');
        }} className="mt-4 text-sm font-medium text-purple-600 hover:text-purple-500" data-id="element-21451" data-mp="1hcr88asvpm18">
              Clear all filters
            </button>
          </div> : <>
            {/* Desktop Table View */}
            <div className={`hidden md:block rounded-lg shadow-sm border overflow-hidden ${dk.card} ${dk.border}`} data-id="element-21452" data-mp="yow1havop8hm">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-21453" data-mp="2a2hqmw1jna35">
                <thead className={dk.bgMuted} data-id="element-21454" data-mp="2f0jh421aubon">
                  <tr data-id="element-21455" data-mp="2ez82f76bfjir">
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21456" data-mp="2f2vxwbvbmg04">
                      Reviewer
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21457" data-mp="3przd8o1q4y3u">
                      Product
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21458" data-mp="3btiutzui1j5v">
                      Rating
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21459" data-mp="31wmuiher83ce">
                      Date
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-21460" data-mp="1ry1fl0nxh36m">
                      Review
                    </th>
                    <th scope="col" className={`relative px-6 py-3`} data-id="element-21461" data-mp="1i6x10225zkcb">
                      <span className="sr-only" data-id="element-21462" data-mp="1d9og3tpmt27d">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${dk.card}`} data-id="element-21463" data-mp="1994m2z0cuepg">
                  {filteredReviews.map(review => <tr key={review.id} className={`hover:${dk.bgMuted}/50 transition-colors`} data-id="element-21464" data-mp="zaf5rao3x2bb">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-21465" data-mp="2tn4xblr05zfj">
                        <div className="flex items-center" data-id="element-21466" data-mp="2tlamx1dcxppp">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${review.reviewerColor}`} data-id="element-21467" data-mp="2opd5erqcydbc">
                            {review.reviewerInitials}
                          </div>
                          <div className="ml-3" data-id="element-21468" data-mp="35xcpcl7yz8b0">
                            <div className={`text-sm font-medium ${dk.textHeading}`} data-id="element-21469" data-mp="35zpdjg4bmewi">
                              {review.reviewerName}
                            </div>
                            {/* Vendors see the same disclosure buyers see */}
                            <DisclosureBadgeGroup disclosures={review.disclosures} subjectType="product" compact maxVisible={1} showIcon={false} className="mt-0.5" data-id="element-21470" data-mp="2i9v292zngyed" />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-21471" data-mp="3dvo4smb3dtft">
                        <div className="flex items-center" data-id="element-21472" data-mp="38yo1uk9jh6sx">
                          <img className="h-10 w-8 rounded object-cover mr-3 shadow-sm" src={review.productCover} alt="" data-id="element-21473" data-mp="390ikfrxc8mtu" />
                          <div className={`text-sm ${dk.textHeading} truncate max-w-[150px]`} data-id="element-21474" data-mp="2v6849yzeq0ze">
                            {review.productTitle}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-21475" data-mp="306489y83tsey">
                        <div className="flex text-amber-400" data-id="element-21476" data-mp="303rfeq0t5xn4">
                          {[...Array(review.rating)].map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-current" data-id="element-21477" data-mp="27hf0jgcjmou8" data-mpi={i} />)}
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textSecondary}`} data-id="element-21478" data-mp="rr5sv7ge6lhi">
                        {review.date}
                      </td>
                      <td className={`px-6 py-4 text-sm ${dk.textSecondary} max-w-xs truncate`} data-id="element-21479" data-mp="hxyqlvgy7lln">
                        {review.excerpt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-21480" data-mp="1amcqyhvieu0d">
                        <div className="flex items-center justify-end space-x-3" data-id="element-21481" data-mp="wraa4zp4jk8s">
                          <button className={`text-gray-400 hover:${dk.textHeading}`} title="View Full Review" data-id="element-21482" data-mp="10opyeslaf3xx">
                            <EyeIcon className="h-4 w-4" data-id="element-21483" data-mp="3li3602jm384a" />
                          </button>
                          <button className={`text-gray-400 hover:text-purple-600`} title="Reply" data-id="element-21484" data-mp="5k3ga0wpasx3">
                            <MessageSquareIcon className="h-4 w-4" data-id="element-21485" data-mp="3ryv41filava0" />
                          </button>
                          <button className={`text-gray-400 hover:text-red-500`} title="Flag" data-id="element-21486" data-mp="3n0bez68p93df">
                            <FlagIcon className="h-4 w-4" data-id="element-21487" data-mp="ji0cuxb5mzbr" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4" data-id="element-21488" data-mp="2z9xp9mdl7gm0">
              {filteredReviews.map(review => <div key={review.id} className={`rounded-lg shadow-sm border p-4 ${dk.card} ${dk.border}`} data-id="element-21489" data-mp="2v7kt9pon6x2f">
                  <div className="flex justify-between items-start mb-3" data-id="element-21490" data-mp="1anywz6ntamwy">
                    <div className="flex items-center gap-3" data-id="element-21491" data-mp="1ern4jpcg1ii7">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${review.reviewerColor}`} data-id="element-21492" data-mp="1jq83llfylw52">
                        {review.reviewerInitials}
                      </div>
                      <div data-id="element-21493" data-mp="1yf4avga6z54m">
                        <h3 className={`text-sm font-medium ${dk.textHeading}`} data-id="element-21494" data-mp="1yebcp3patny3">
                          {review.reviewerName}
                        </h3>
                        <p className={`text-xs ${dk.textMuted}`} data-id="element-21495" data-mp="22f4x3n3j8km8">
                          {review.date}
                        </p>
                        <DisclosureBadgeGroup disclosures={review.disclosures} subjectType="product" compact maxVisible={1} showIcon={false} className="mt-1" data-id="element-21496" data-mp="83qb9hdn75br" />
                      </div>
                    </div>
                    <div className="flex text-amber-400" data-id="element-21497" data-mp="1pe55hfa2lq8y">
                      {[...Array(review.rating)].map((_, i) => <StarIcon key={i} className="h-3 w-3 fill-current" data-id="element-21498" data-mp="1kdr7kq26nwon" data-mpi={i} />)}
                    </div>
                  </div>

                  <div className="mb-3" data-id="element-21499" data-mp="1ucq3r95rhzur">
                    <p className={`text-xs font-medium mb-1 ${dk.textPrimary}`} data-id="element-21500" data-mp="1ubnc2z0m7tds">
                      {review.productTitle}
                    </p>
                    <p className={`text-sm ${dk.textSecondary} line-clamp-2`} data-id="element-21501" data-mp="285y01e4sfjis">
                      "{review.excerpt}"
                    </p>
                  </div>

                  <div className="flex items-center justify-end gap-4 border-t pt-3 border-gray-100 dark:border-gray-700" data-id="element-21502" data-mp="2i2srjd2n8jnk">
                    <button className="text-xs font-medium text-purple-600 hover:text-purple-500 flex items-center" data-id="element-21503" data-mp="2d1d7noc7cj6r">
                      <EyeIcon className="h-3 w-3 mr-1" data-id="element-21504" data-mp="31nt21syxfddr" /> View Full
                    </button>
                    <button className={`text-xs font-medium ${dk.textSecondary} hover:${dk.textPrimary} flex items-center`} data-id="element-21505" data-mp="2mqtv87bwfokc">
                      <MessageSquareIcon className="h-3 w-3 mr-1" data-id="element-21506" data-mp="2ro293vq0l6fs" /> Reply
                    </button>
                  </div>
                </div>)}
            </div>
          </>}
      </div>
    </div>;
};
export default ProductReviews;